"use client";

import { captureException } from "@sentry/nextjs";
import type { WhisperJson } from "@teksta/shared-types";
import Lunr from "lunr";
import { useTranslations } from "next-intl";
import { ofetch } from "ofetch";
import { useEffect, useState } from "react";
import { SubtitleViewerLite } from "@/components/transcriptions/subtitle-viewer/SubtitleViewerLite";
import { getPublicBucketBaseUrl } from "@/lib/storage";

export const EpisodeTranscriptViewer = ({
  transcriptionRunId,
  whisperJson,
  videoId,
}: {
  transcriptionRunId: string;
  whisperJson: WhisperJson;
  videoId: string;
}) => {
  const t = useTranslations();
  const [searchStarted, setSearchStarted] = useState(false);
  const [search, setSearch] = useState<{ index?: object; error?: boolean }>({});
  useEffect(() => {
    if (!searchStarted) return;
    const controller = new AbortController();
    setSearch({});
    const loadIndex = async () => {
      try {
        const index = await ofetch<object>(
          `${getPublicBucketBaseUrl()}/transcription_runs/${transcriptionRunId}/lunr_index.json`,
          {
            signal: AbortSignal.any([controller.signal, AbortSignal.timeout(6000)]),
            retry: 0,
          }
        );
        // Treat malformed indexes like unavailable search, without breaking the player.
        Lunr.Index.load(index);
        if (!controller.signal.aborted) setSearch({ index });
      } catch (error) {
        if (!controller.signal.aborted) {
          captureException(error, { tags: { feature: "podcast-transcript-search" } });
          setSearch({ error: true });
        }
      }
    };
    void loadIndex();
    return () => controller.abort();
  }, [searchStarted, transcriptionRunId]);

  return (
    <SubtitleViewerLite
      whisperJson={whisperJson}
      videoId={videoId}
      lunrIndex={search.index}
      onSearch={(query) => {
        if (query.trim()) setSearchStarted(true);
      }}
      searchStatus={
        <p role="status" className="text-sm text-foreground/80">
          {search.error
            ? t("podcasts.searchUnavailable")
            : searchStarted && !search.index
              ? t("general.loadingLabel")
              : ""}
        </p>
      }
    />
  );
};
